// Centralized portfolio data source
// All values are in USD

// Asset holdings with prices
export const assetHoldings = [
  {
    symbol: 'BTC',
    name: 'Bitcoin',
    logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg',
    spotAmount: 0.267,
    marginAmount: 0,
    price: 95532.0,
  },
  {
    symbol: 'ETH',
    name: 'Ethereum',
    logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg',
    spotAmount: 3.802,
    marginAmount: 0,
    price: 3287.0,
  },
  {
    symbol: 'USDT',
    name: 'Tether',
    logo: 'https://cryptologos.cc/logos/tether-usdt-logo.svg',
    spotAmount: 7503.75,
    marginAmount: 0,
    price: 0.9995,
  },
  {
    symbol: 'SOL',
    name: 'Solana',
    logo: 'https://cryptologos.cc/logos/solana-sol-logo.svg',
    spotAmount: 21.23,
    marginAmount: 0,
    price: 141.33,
  },
  {
    symbol: 'BNB',
    name: 'BNB',
    logo: 'https://cryptologos.cc/logos/bnb-bnb-logo.svg',
    spotAmount: 2.31,
    marginAmount: 0,
    price: 650.0,
  },
]

// Calculate spot balance
export const spotBalance = assetHoldings.reduce(
  (total, asset) => total + asset.spotAmount * asset.price,
  0,
)

// Spot unrealized P&L
export const spotUpnl = 1250.45

// Margin positions
export interface MarginPosition {
  market: string
  symbol: string
  logo: string
  side: 'Long' | 'Short'
  openQuantity: number
  openingPrice: number
  currentPrice: number
  estLiquidation: number
  initialMargin: number
  leverage: number
  unrealizedPnl: number
  unrealizedPnlPercent: number
}

export const marginPositions: MarginPosition[] = [
  {
    market: 'BTC/USD',
    symbol: 'BTC',
    logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg',
    side: 'Long',
    openQuantity: 0.0008267,
    openingPrice: 95790.6,
    currentPrice: 95656.8,
    estLiquidation: 90228.7,
    initialMargin: 7.91,
    leverage: 10,
    unrealizedPnl: 125.5,
    unrealizedPnlPercent: 15.87,
  },
]

// Futures positions
export interface FuturesPosition {
  symbol: string
  pair: string
  logo: string
  side: 'Long' | 'Short'
  size: number
  entryPrice: number
  markPrice: number
  unrealizedPnl: number
  unrealizedPnlPercent: number
  leverage: number
}

export const futuresPositions: FuturesPosition[] = [
  {
    symbol: 'BTC',
    pair: 'BTC/USD',
    logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg',
    side: 'Long',
    size: 0.5,
    entryPrice: 94250.0,
    markPrice: 95532.0,
    unrealizedPnl: 641.0,
    unrealizedPnlPercent: 1.36,
    leverage: 5,
  },
  {
    symbol: 'ETH',
    pair: 'ETH/USD',
    logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg',
    side: 'Short',
    size: 2.5,
    entryPrice: 3350.0,
    markPrice: 3287.0,
    unrealizedPnl: 157.5,
    unrealizedPnlPercent: 1.88,
    leverage: 3,
  },
]

// Calculate futures unrealized P&L
export const futuresUnrealizedPnl = futuresPositions.reduce(
  (sum, pos) => sum + pos.unrealizedPnl,
  0,
)

// Calculate margin positions unrealized P&L
export const marginPositionsUpnl = marginPositions.reduce(
  (sum, pos) => sum + pos.unrealizedPnl,
  0,
)

// Margin account values
export const marginBalance = 5110.78
export const marginUsedMargin = 2550.12
export const maxLeverage = 10

// Futures account values
export const futuresBalance = 5000.0 // Base futures balance
export const futuresAccountValue = futuresBalance + futuresUnrealizedPnl

// Earn account values
export const earnBalance = 123.45
export const earnRewards = 123.45

// Calculate available margin
export const availableMargin = marginBalance * maxLeverage - marginUsedMargin

// Calculate current leverage
export const currentLeverage =
  marginBalance > 0 ? marginUsedMargin / marginBalance : 0

// Main account value = spot + spot UP&L + margin + margin UP&L + earn
export const mainAccountValue =
  spotBalance + spotUpnl + marginBalance + marginPositionsUpnl + earnBalance

// Total portfolio value = main account + futures account
export const totalPortfolioValue = mainAccountValue + futuresAccountValue

// Market data with 24h price history for sparklines
export const marketData = [
  {
    name: 'Bitcoin',
    symbol: 'BTC/USD',
    logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg',
    leverage: '10x',
    price: 95407.8,
    low24h: 95101.0,
    high24h: 97092.9,
    change24h: -1.72,
    volume24h: '150M',
    isFavorite: true,
    priceHistory: [
      97800, 97650, 97900, 98100, 97950, 97700, 97500, 97350, 97200, 97000,
      96850, 96950, 97100, 96900, 96700, 96500, 96350, 96200, 96050, 95900,
      95750, 95650, 95580, 95407.8,
    ],
  },
  {
    name: 'Ethereum',
    symbol: 'ETH/USD',
    logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg',
    leverage: '10x',
    price: 3303.58,
    low24h: 3272.92,
    high24h: 3381.4,
    change24h: -2.27,
    volume24h: '55.9M',
    isFavorite: true,
    priceHistory: [
      3365, 3358, 3370, 3380, 3372, 3360, 3350, 3342, 3335, 3328, 3320, 3325,
      3330, 3318, 3310, 3302, 3295, 3290, 3285, 3292, 3288, 3290, 3289, 3303.58,
    ],
  },
  {
    name: 'Bitcoin Perpetual',
    symbol: 'BTC Perp',
    logo: 'https://cryptologos.cc/logos/bitcoin-btc-logo.svg',
    leverage: '50x',
    price: 95428,
    low24h: 95110,
    high24h: 97150,
    change24h: -1.71,
    volume24h: '184M',
    isFavorite: true,
    priceHistory: [
      97800, 97650, 97900, 98100, 97950, 97700, 97500, 97350, 97200, 97000,
      96850, 96950, 97100, 96900, 96700, 96500, 96350, 96200, 96050, 95900,
      95750, 95650, 95580, 95428,
    ],
  },
  {
    name: 'Solana',
    symbol: 'SOL/USD',
    logo: 'https://cryptologos.cc/logos/solana-sol-logo.svg',
    leverage: '10x',
    price: 143.06,
    low24h: 140.96,
    high24h: 145.61,
    change24h: -1.65,
    volume24h: '28.4M',
    isFavorite: true,
    priceHistory: [
      145.2, 145.0, 144.8, 144.5, 144.2, 144.0, 143.8, 143.5, 143.3, 143.1,
      142.9, 142.8, 142.9, 143.0, 143.2, 143.1, 143.0, 142.8, 142.9, 143.0,
      143.1, 143.2, 143.1, 143.06,
    ],
  },
  {
    name: 'Cardano',
    symbol: 'ADA/USD',
    logo: 'https://cryptologos.cc/logos/cardano-ada-logo.svg',
    leverage: '10x',
    price: 0.4523,
    low24h: 0.4401,
    high24h: 0.4685,
    change24h: 1.85,
    volume24h: '12.3M',
    isFavorite: true,
    priceHistory: [
      0.445, 0.448, 0.451, 0.455, 0.458, 0.462, 0.465, 0.463, 0.46, 0.457,
      0.454, 0.451, 0.448, 0.445, 0.447, 0.449, 0.451, 0.453, 0.452, 0.451,
      0.45, 0.451, 0.452, 0.4523,
    ],
  },
  {
    name: 'Ripple',
    symbol: 'XRP/USD',
    logo: 'https://cryptologos.cc/logos/xrp-xrp-logo.svg',
    leverage: '10x',
    price: 0.5847,
    low24h: 0.5721,
    high24h: 0.5982,
    change24h: -0.95,
    volume24h: '18.7M',
    isFavorite: true,
    priceHistory: [
      0.59, 0.592, 0.595, 0.598, 0.596, 0.593, 0.59, 0.588, 0.586, 0.584, 0.582,
      0.58, 0.578, 0.576, 0.578, 0.58, 0.582, 0.583, 0.584, 0.585, 0.586, 0.585,
      0.585, 0.5847,
    ],
  },
  {
    name: 'Polkadot',
    symbol: 'DOT/USD',
    logo: 'https://cryptologos.cc/logos/polkadot-new-dot-logo.svg',
    leverage: '10x',
    price: 6.82,
    low24h: 6.65,
    high24h: 7.05,
    change24h: 2.34,
    volume24h: '8.9M',
    isFavorite: true,
    priceHistory: [
      6.65, 6.68, 6.72, 6.75, 6.78, 6.82, 6.85, 6.88, 6.9, 6.92, 6.95, 6.98,
      7.0, 7.02, 7.0, 6.98, 6.95, 6.92, 6.9, 6.88, 6.85, 6.83, 6.82, 6.82,
    ],
  },
  {
    name: 'Avalanche',
    symbol: 'AVAX/USD',
    logo: 'https://cryptologos.cc/logos/avalanche-avax-logo.svg',
    leverage: '10x',
    price: 34.52,
    low24h: 33.85,
    high24h: 35.78,
    change24h: -1.28,
    volume24h: '15.2M',
    isFavorite: true,
    priceHistory: [
      35.2, 35.4, 35.6, 35.8, 35.7, 35.5, 35.3, 35.1, 34.9, 34.7, 34.5, 34.3,
      34.2, 34.1, 34.2, 34.3, 34.4, 34.5, 34.6, 34.5, 34.5, 34.5, 34.52, 34.52,
    ],
  },
  {
    name: 'Chainlink',
    symbol: 'LINK/USD',
    logo: 'https://cryptologos.cc/logos/chainlink-link-logo.svg',
    leverage: '10x',
    price: 14.87,
    low24h: 14.52,
    high24h: 15.23,
    change24h: 1.52,
    volume24h: '11.4M',
    isFavorite: true,
    priceHistory: [
      14.65, 14.7, 14.75, 14.8, 14.85, 14.9, 14.95, 15.0, 15.05, 15.1, 15.15,
      15.2, 15.18, 15.15, 15.1, 15.05, 15.0, 14.95, 14.92, 14.9, 14.88, 14.87,
      14.87, 14.87,
    ],
  },
  {
    name: 'Polygon',
    symbol: 'MATIC/USD',
    logo: 'https://cryptologos.cc/logos/polygon-matic-logo.svg',
    leverage: '10x',
    price: 0.8234,
    low24h: 0.8012,
    high24h: 0.8456,
    change24h: 0.87,
    volume24h: '9.8M',
    isFavorite: true,
    priceHistory: [
      0.815, 0.818, 0.822, 0.825, 0.828, 0.832, 0.835, 0.838, 0.84, 0.842,
      0.844, 0.845, 0.843, 0.84, 0.838, 0.835, 0.832, 0.83, 0.828, 0.826, 0.825,
      0.824, 0.823, 0.8234,
    ],
  },
]

// Unified color palette
export const colors = {
  success: '#00C076',
  danger: '#E85D75', // Softer red
  warning: '#F59E0B',
  brandPurple: '#8B5CF6',
}
